import React, { useState, useEffect } from 'react';
import { Settings, Database, Shield, Bell, Globe, Users, Clock, HardDrive, Network, Save, RefreshCw, AlertTriangle, CheckCircle, Info, Palette } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/common/LanguageSwitcher';
import BrandingSettings from './BrandingSettings';
import { logger } from '@/utils/logger'

interface GeneralSettings {
  siteName: string;
  siteDescription: string;
  adminEmail: string;
  timezone: string;
  language: string;
  dateFormat: string;
  currency: string;
  maintenanceMode: boolean;
}

interface SecuritySettings {
  sessionTimeout: number;
  passwordMinLength: number;
  passwordComplexity: boolean;
  twoFactorAuth: boolean;
  maxLoginAttempts: number;
  lockoutDuration: number;
  auditLogging: boolean;
  encryptionEnabled: boolean;
}

interface NotificationSettings {
  emailNotifications: boolean;
  smsNotifications: boolean;
  systemAlerts: boolean;
  deviceAlerts: boolean;
  maintenanceAlerts: boolean;
  reportNotifications: boolean;
  emergencyAlerts: boolean;
  notificationRetention: number;
}

interface DatabaseSettings {
  backupFrequency: string;
  backupRetention: number;
  autoOptimization: boolean;
  connectionPool: number;
  queryTimeout: number;
  indexMaintenance: boolean;
  compressionEnabled: boolean;
  replicationEnabled: boolean;
}

interface IntegrationSettings {
  dicomStorageLimit: number;
  hl7Enabled: boolean;
  hl7Port: number;
  fhirEnabled: boolean;
  fhirEndpoint: string;
  externalApiTimeout: number;
  webhookRetries: number;
  cloudSyncEnabled: boolean;
}

interface SystemInfo {
  version: string;
  buildDate: string;
  uptime: string;
  totalStorage: string;
  usedStorage: string;
  memoryUsage: string;
  cpuUsage: string;
  activeUsers: number;
  totalUsers: number;
  lastBackup: string;
}

const SystemSettings: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [generalSettings, setGeneralSettings] = useState<GeneralSettings>({
    siteName: 'PIS - Pathology Information System',
    siteDescription: 'Advanced digital pathology and laboratory workflow platform',
    adminEmail: 'admin@hospital.com',
    timezone: 'America/New_York',
    language: 'en',
    dateFormat: 'MM/DD/YYYY',
    currency: 'USD',
    maintenanceMode: false
  });

  const [securitySettings, setSecuritySettings] = useState<SecuritySettings>({
    sessionTimeout: 30,
    passwordMinLength: 8,
    passwordComplexity: true,
    twoFactorAuth: false,
    maxLoginAttempts: 5,
    lockoutDuration: 15,
    auditLogging: true,
    encryptionEnabled: true
  });

  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    emailNotifications: true,
    smsNotifications: false,
    systemAlerts: true,
    deviceAlerts: true,
    maintenanceAlerts: true,
    reportNotifications: true,
    emergencyAlerts: true,
    notificationRetention: 90
  });

  const [databaseSettings, setDatabaseSettings] = useState<DatabaseSettings>({
    backupFrequency: 'daily',
    backupRetention: 30,
    autoOptimization: true,
    connectionPool: 20,
    queryTimeout: 30,
    indexMaintenance: true,
    compressionEnabled: true,
    replicationEnabled: false
  });

  const [integrationSettings, setIntegrationSettings] = useState<IntegrationSettings>({
    dicomStorageLimit: 1000,
    hl7Enabled: true,
    hl7Port: 2575,
    fhirEnabled: false,
    fhirEndpoint: '',
    externalApiTimeout: 30,
    webhookRetries: 3,
    cloudSyncEnabled: false
  });

  const [systemInfo] = useState<SystemInfo>({
    version: '2.4.1',
    buildDate: '2024-01-29',
    uptime: '15 days, 8 hours',
    totalStorage: '2.5 TB',
    usedStorage: '1.2 TB',
    memoryUsage: '68%',
    cpuUsage: '23%',
    activeUsers: 45,
    totalUsers: 156,
    lastBackup: '2024-01-29 02:00:00'
  });

  const [activeTab, setActiveTab] = useState<'general' | 'branding' | 'security' | 'notifications' | 'database' | 'integrations' | 'system'>('general');
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error', message: string } | null>(null);

  useEffect(() => {
    // Simulate fetching settings from API
    fetchSystemInfo();
  }, []);

  const fetchSystemInfo = async () => {
    // Simulate API call to get current system information
    try {
      // This would be a real API call in production
      logger.debug('Fetching system information...');
    } catch (error) {
      console.error('Error fetching system info:', error);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      // Simulate API call to save settings
      await new Promise(resolve => setTimeout(resolve, 1000));

      setSaveMessage({ type: 'success', message: 'Settings saved successfully!' });
      setHasChanges(false);

      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      setSaveMessage({ type: 'error', message: 'Failed to save settings. Please try again.' });
      setTimeout(() => setSaveMessage(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async (type: string) => {
    try {
      // Simulate connection test
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSaveMessage({ type: 'success', message: `${type} connection test successful!` });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      setSaveMessage({ type: 'error', message: `${type} connection test failed.` });
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  const handleManualBackup = async () => {
    try {
      setSaving(true);
      await new Promise(resolve => setTimeout(resolve, 3000));
      setSaveMessage({ type: 'success', message: 'Manual backup completed successfully!' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      setSaveMessage({ type: 'error', message: 'Manual backup failed.' });
      setTimeout(() => setSaveMessage(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const markAsChanged = () => {
    setHasChanges(true);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">{t('settings.title')}</h1>
        <p className="text-gray-600">{t('settings.description')}</p>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div className={`mb-6 p-4 rounded-lg border ${
          saveMessage.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <div className="flex items-center">
            {saveMessage.type === 'success' ? (
              <CheckCircle className="w-5 h-5 mr-2" />
            ) : (
              <AlertTriangle className="w-5 h-5 mr-2" />
            )}
            {saveMessage.message}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg overflow-x-auto">
        {[
          { id: 'general', label: t('settings.general'), icon: Settings },
          { id: 'branding', label: 'Branding', icon: Palette },
          { id: 'security', label: t('settings.security'), icon: Shield },
          { id: 'notifications', label: t('settings.notifications'), icon: Bell },
          { id: 'database', label: t('settings.database'), icon: Database },
          { id: 'integrations', label: t('settings.integrations'), icon: Network },
          { id: 'system', label: t('settings.systemInfo'), icon: Info }
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Icon className="w-4 h-4 mr-2" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* General Settings */}
      {activeTab === 'general' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-semibold mb-6">{t('settings.general')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings.siteName')}</label>
              <input
                type="text"
                value={generalSettings.siteName}
                onChange={(e) => {
                  setGeneralSettings({ ...generalSettings, siteName: e.target.value });
                  markAsChanged();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings.adminEmail')}</label>
              <input
                type="email"
                value={generalSettings.adminEmail}
                onChange={(e) => {
                  setGeneralSettings({ ...generalSettings, adminEmail: e.target.value });
                  markAsChanged();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings.timezone')}</label>
              <select
                value={generalSettings.timezone}
                onChange={(e) => {
                  setGeneralSettings({ ...generalSettings, timezone: e.target.value });
                  markAsChanged();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="America/New_York">Eastern Time</option>
                <option value="America/Chicago">Central Time</option>
                <option value="America/Denver">Mountain Time</option>
                <option value="America/Los_Angeles">Pacific Time</option>
                <option value="UTC">UTC</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Language / Ngôn ngữ</label>
              <div className="flex items-center space-x-4">
                <LanguageSwitcher />
                <span className="text-sm text-gray-500">
                  {t('settings.currentLanguage')}: {i18n.language === 'vi' ? '🇻🇳 Tiếng Việt' : '🇬🇧 English'}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {i18n.language === 'vi' ? 'Thay đổi áp dụng ngay lập tức cho toàn hệ thống' : 'Changes apply immediately to the entire system'}
              </p>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings.siteDescription')}</label>
              <textarea
                value={generalSettings.siteDescription}
                onChange={(e) => {
                  setGeneralSettings({ ...generalSettings, siteDescription: e.target.value });
                  markAsChanged();
                }}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="md:col-span-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={generalSettings.maintenanceMode}
                  onChange={(e) => {
                    setGeneralSettings({ ...generalSettings, maintenanceMode: e.target.checked });
                    markAsChanged();
                  }}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700">{t('settings.maintenanceMode')}</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">{t('settings.maintenanceModeDesc')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Branding Settings */}
      {activeTab === 'branding' && (
        <BrandingSettings />
      )}

      {/* Security Settings */}
      {activeTab === 'security' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-semibold mb-6">Security Settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Session Timeout (minutes)</label>
              <input
                type="number"
                value={securitySettings.sessionTimeout}
                onChange={(e) => {
                  setSecuritySettings({ ...securitySettings, sessionTimeout: Number(e.target.value) });
                  markAsChanged();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password Min Length</label>
              <input
                type="number"
                value={securitySettings.passwordMinLength}
                onChange={(e) => {
                  setSecuritySettings({ ...securitySettings, passwordMinLength: Number(e.target.value) });
                  markAsChanged();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Max Login Attempts</label>
              <input
                type="number"
                value={securitySettings.maxLoginAttempts}
                onChange={(e) => {
                  setSecuritySettings({ ...securitySettings, maxLoginAttempts: Number(e.target.value) });
                  markAsChanged();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Lockout Duration (minutes)</label>
              <input
                type="number"
                value={securitySettings.lockoutDuration}
                onChange={(e) => {
                  setSecuritySettings({ ...securitySettings, lockoutDuration: Number(e.target.value) });
                  markAsChanged();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { key: 'passwordComplexity', label: 'Password Complexity Required' },
                { key: 'twoFactorAuth', label: 'Two-Factor Authentication' },
                { key: 'auditLogging', label: 'Audit Logging' },
                { key: 'encryptionEnabled', label: 'Data Encryption' }
              ].map((setting) => (
                <label key={setting.key} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={securitySettings[setting.key as keyof SecuritySettings] as boolean}
                    onChange={(e) => {
                      setSecuritySettings({ ...securitySettings, [setting.key]: e.target.checked });
                      markAsChanged();
                    }}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">{setting.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Notification Settings */}
      {activeTab === 'notifications' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-semibold mb-6">Notification Settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <h3 className="text-lg font-medium mb-4">Notification Types</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: 'emailNotifications', label: 'Email Notifications' },
                  { key: 'smsNotifications', label: 'SMS Notifications' },
                  { key: 'systemAlerts', label: 'System Alerts' },
                  { key: 'deviceAlerts', label: 'Device Alerts' },
                  { key: 'maintenanceAlerts', label: 'Maintenance Alerts' },
                  { key: 'reportNotifications', label: 'Report Notifications' },
                  { key: 'emergencyAlerts', label: 'Emergency Alerts' }
                ].map((setting) => (
                  <label key={setting.key} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={notificationSettings[setting.key as keyof NotificationSettings] as boolean}
                      onChange={(e) => {
                        setNotificationSettings({ ...notificationSettings, [setting.key]: e.target.checked });
                        markAsChanged();
                      }}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">{setting.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notification Retention (days)</label>
              <input
                type="number"
                value={notificationSettings.notificationRetention}
                onChange={(e) => {
                  setNotificationSettings({ ...notificationSettings, notificationRetention: Number(e.target.value) });
                  markAsChanged();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      )}

      {/* Database Settings */}
      {activeTab === 'database' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-semibold mb-6">Database Settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Backup Frequency</label>
              <select
                value={databaseSettings.backupFrequency}
                onChange={(e) => {
                  setDatabaseSettings({ ...databaseSettings, backupFrequency: e.target.value });
                  markAsChanged();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Backup Retention (days)</label>
              <input
                type="number"
                value={databaseSettings.backupRetention}
                onChange={(e) => {
                  setDatabaseSettings({ ...databaseSettings, backupRetention: Number(e.target.value) });
                  markAsChanged();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Connection Pool Size</label>
              <input
                type="number"
                value={databaseSettings.connectionPool}
                onChange={(e) => {
                  setDatabaseSettings({ ...databaseSettings, connectionPool: Number(e.target.value) });
                  markAsChanged();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Query Timeout (seconds)</label>
              <input
                type="number"
                value={databaseSettings.queryTimeout}
                onChange={(e) => {
                  setDatabaseSettings({ ...databaseSettings, queryTimeout: Number(e.target.value) });
                  markAsChanged();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { key: 'autoOptimization', label: 'Auto Optimization' },
                { key: 'indexMaintenance', label: 'Index Maintenance' },
                { key: 'compressionEnabled', label: 'Compression Enabled' },
                { key: 'replicationEnabled', label: 'Replication Enabled' }
              ].map((setting) => (
                <label key={setting.key} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={databaseSettings[setting.key as keyof DatabaseSettings] as boolean}
                    onChange={(e) => {
                      setDatabaseSettings({ ...databaseSettings, [setting.key]: e.target.checked });
                      markAsChanged();
                    }}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">{setting.label}</span>
                </label>
              ))}
            </div>
            <div className="md:col-span-2">
              <button
                onClick={handleManualBackup}
                disabled={saving}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
              >
                <HardDrive className="w-4 h-4 mr-2" />
                {saving ? 'Creating Backup...' : 'Create Manual Backup'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Integration Settings */}
      {activeTab === 'integrations' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-semibold mb-6">Integration Settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">DICOM Storage Limit (GB)</label>
              <input
                type="number"
                value={integrationSettings.dicomStorageLimit}
                onChange={(e) => {
                  setIntegrationSettings({ ...integrationSettings, dicomStorageLimit: Number(e.target.value) });
                  markAsChanged();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">HL7 Port</label>
              <input
                type="number"
                value={integrationSettings.hl7Port}
                onChange={(e) => {
                  setIntegrationSettings({ ...integrationSettings, hl7Port: Number(e.target.value) });
                  markAsChanged();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">External API Timeout (seconds)</label>
              <input
                type="number"
                value={integrationSettings.externalApiTimeout}
                onChange={(e) => {
                  setIntegrationSettings({ ...integrationSettings, externalApiTimeout: Number(e.target.value) });
                  markAsChanged();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Webhook Retries</label>
              <input
                type="number"
                value={integrationSettings.webhookRetries}
                onChange={(e) => {
                  setIntegrationSettings({ ...integrationSettings, webhookRetries: Number(e.target.value) });
                  markAsChanged();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">FHIR Endpoint</label>
              <input
                type="url"
                value={integrationSettings.fhirEndpoint}
                onChange={(e) => {
                  setIntegrationSettings({ ...integrationSettings, fhirEndpoint: e.target.value });
                  markAsChanged();
                }}
                placeholder="https://fhir.example.com/api"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { key: 'hl7Enabled', label: 'HL7 Integration' },
                { key: 'fhirEnabled', label: 'FHIR Integration' },
                { key: 'cloudSyncEnabled', label: 'Cloud Sync' }
              ].map((setting) => (
                <label key={setting.key} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={integrationSettings[setting.key as keyof IntegrationSettings] as boolean}
                    onChange={(e) => {
                      setIntegrationSettings({ ...integrationSettings, [setting.key]: e.target.checked });
                      markAsChanged();
                    }}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">{setting.label}</span>
                </label>
              ))}
            </div>
            <div className="md:col-span-2 flex space-x-4">
              <button
                onClick={() => handleTestConnection('HL7')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
              >
                <Network className="w-4 h-4 mr-2" />
                Test HL7 Connection
              </button>
              <button
                onClick={() => handleTestConnection('FHIR')}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center"
              >
                <Globe className="w-4 h-4 mr-2" />
                Test FHIR Connection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* System Info */}
      {activeTab === 'system' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-semibold mb-6">System Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { label: 'Version', value: systemInfo.version, icon: Info },
              { label: 'Build Date', value: systemInfo.buildDate, icon: Clock },
              { label: 'Uptime', value: systemInfo.uptime, icon: Clock },
              { label: 'Total Storage', value: systemInfo.totalStorage, icon: HardDrive },
              { label: 'Used Storage', value: systemInfo.usedStorage, icon: HardDrive },
              { label: 'Memory Usage', value: systemInfo.memoryUsage, icon: Settings },
              { label: 'CPU Usage', value: systemInfo.cpuUsage, icon: Settings },
              { label: 'Active Users', value: systemInfo.activeUsers.toString(), icon: Users },
              { label: 'Total Users', value: systemInfo.totalUsers.toString(), icon: Users },
              { label: 'Last Backup', value: systemInfo.lastBackup, icon: Database }
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.label} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <Icon className="w-5 h-5 text-gray-600 mr-2" />
                    <span className="text-sm font-medium text-gray-600">{item.label}</span>
                  </div>
                  <div className="text-lg font-semibold text-gray-900">{item.value}</div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={fetchSystemInfo}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh System Info
            </button>
          </div>
        </div>
      )}

      {/* Save Button */}
      {activeTab !== 'system' && activeTab !== 'branding' && (
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSaveSettings}
            disabled={!hasChanges || saving}
            className={`px-6 py-2 rounded-lg font-medium flex items-center ${
              hasChanges && !saving
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? t('settings.saving') : t('settings.saveSettings')}
          </button>
        </div>
      )}
    </div>
  );
};

export default SystemSettings;